import { useEffect, useState, type FormEvent } from "react";
import {
  APIError,
  browserSessionRequiredEvent,
  createBrowserSession,
  deleteBrowserSession,
  restoreBrowserSession,
} from "./api";
import { EditorApp } from "./EditorApp";
import "./session-gate.css";

type GatePhase = "checking" | "required" | "ready";

export function App() {
  const [phase, setPhase] = useState<GatePhase>("checking");
  const [sessionEnabled, setSessionEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [apiKey, setAPIKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const requireSession = () => {
      setSessionEnabled(true);
      setPhase("required");
      setError("Your session expired or was revoked. Authenticate again to continue.");
    };
    window.addEventListener(browserSessionRequiredEvent(), requireSession);
    void checkSession();
    return () => window.removeEventListener(browserSessionRequiredEvent(), requireSession);
  }, []);

  async function checkSession() {
    setPhase("checking");
    setError("");
    try {
      const state = await restoreBrowserSession();
      setSessionEnabled(state.mode !== "disabled");
      setPhase(state.mode === "required" ? "required" : "ready");
    } catch (cause) {
      setSessionEnabled(true);
      setPhase("required");
      setError(describeError(cause, "The BEP service could not verify this browser session."));
    }
  }

  async function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const credential = apiKey.trim();
    if (!credential) {
      setError("Enter the API key issued for this Zeitpoint workspace.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await createBrowserSession(credential);
      setAPIKey("");
      setSessionEnabled(true);
      setPhase("ready");
    } catch (cause) {
      setAPIKey("");
      setError(describeError(cause, "The API key could not be exchanged for a browser session."));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    setBusy(true);
    setError("");
    try {
      await deleteBrowserSession();
      setPhase("required");
    } catch (cause) {
      setPhase("required");
      setError(describeError(cause, "The server could not revoke this browser session. Select Retry and try signing out again."));
    } finally {
      setBusy(false);
    }
  }

  if (phase === "checking") return <SessionChecking />;
  if (phase === "required") {
    return (
      <SessionGate
        apiKey={apiKey}
        busy={busy}
        error={error}
        onAPIKeyChange={setAPIKey}
        onSubmit={signIn}
        onRetry={() => void checkSession()}
      />
    );
  }
  return sessionEnabled ? <EditorApp onSignOut={() => void signOut()} /> : <EditorApp />;
}

function SessionChecking() {
  return (
    <main className="session-gate" aria-busy="true">
      <section className="session-card" aria-live="polite">
        <div className="session-card__pulse" aria-hidden="true" />
        <span className="session-card__index">BEP / secure session</span>
        <h1>ZEITPOINT</h1>
        <p>Verifying the resident display session…</p>
      </section>
    </main>
  );
}

function SessionGate({
  apiKey,
  busy,
  error,
  onAPIKeyChange,
  onSubmit,
  onRetry,
}: {
  apiKey: string;
  busy: boolean;
  error: string;
  onAPIKeyChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onRetry: () => void;
}) {
  return (
    <main className="session-gate">
      <section className="session-card">
        <span className="session-card__index">BEP / credential exchange</span>
        <h1>OPEN WORKSPACE</h1>
        <p>Exchange an issued API key for a short-lived, host-only browser session.</p>
        <form onSubmit={onSubmit}>
          <label htmlFor="bep-api-key">Workspace API key</label>
          <input
            id="bep-api-key"
            name="api-key"
            type="password"
            autoComplete="off"
            spellCheck="false"
            value={apiKey}
            onChange={(event) => onAPIKeyChange(event.target.value)}
            disabled={busy}
            aria-invalid={error ? "true" : undefined}
          />
          {error && <div className="session-card__error" role="alert">{error}</div>}
          <div className="session-card__actions">
            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? "Opening…" : "Open secure session"}
            </button>
            <button className="button button--quiet" type="button" onClick={onRetry} disabled={busy}>Retry</button>
          </div>
        </form>
        <div className="session-card__note">
          The API key is sent once over HTTPS and is never written to browser storage. The resulting credential is an HttpOnly, SameSite=Strict cookie.
        </div>
      </section>
    </main>
  );
}

function describeError(cause: unknown, fallback: string): string {
  if (cause instanceof APIError) return cause.status === 401 ? "The API key is invalid, expired, or revoked." : cause.detail;
  return cause instanceof Error ? cause.message : fallback;
}
